import { PRIORITY_SEGMENT } from "../data/priorityColors";
import type { Priority } from "../data/types";

/** One run of effort on the bar. */
export interface BarSegment {
  id: string;
  priority: Priority;
  days: number;
  /** In progress now: painted solid. Queued work is painted lighter. */
  active: boolean;
}

/**
 * One person's committed effort against their own capacity.
 *
 * Shared scale, personal line: `scale` is the same for every row so bar
 * lengths compare (LoadBar's rule), while the capacity marker sits at each
 * person's own x. `role="img"` with the whole sentence as its label — this
 * bar is not inside a link that carries the numbers, as LoadBar's is.
 */
export function CapacityBar({
  segments,
  capacityDays,
  scale,
  unestimated,
  label,
}: {
  segments: BarSegment[];
  capacityDays: number;
  scale: number;
  unestimated: number;
  label: string;
}) {
  const denominator = scale > 0 ? scale : 1;
  const pct = (days: number) => `${Math.min(100, (days / denominator) * 100)}%`;
  const committed = segments.reduce((n, s) => n + s.days, 0);
  const marker = Math.min(100, (capacityDays / denominator) * 100);
  const over = committed > capacityDays;

  return (
    <span role="img" aria-label={label} className="relative block h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
      {/* Never clipped: effort past the marker is the number this page exists to show. */}
      {over && <i className="absolute inset-y-0 bg-error-100" style={{ left: `${marker}%`, width: pct(committed - capacityDays) }} />}
      <span className="relative flex h-full">
        {segments.map((s) =>
          s.days > 0 ? <i key={s.id} className={`block h-full flex-none ${PRIORITY_SEGMENT[s.priority]} ${s.active ? "" : "opacity-60"}`} style={{ width: pct(s.days) }} /> : null,
        )}
        {/* Unsized work is a hatched tail of fixed width: its true width is unknown. */}
        {Array.from({ length: unestimated }, (_, i) => (
          <i key={`u${i}`} className="block h-full w-3 flex-none bg-[repeating-linear-gradient(45deg,var(--gray-400)_0_2px,transparent_2px_5px)]" />
        ))}
      </span>
      {capacityDays > 0 && <i className="absolute inset-y-0 w-0.5 bg-gray-900" style={{ left: `calc(${marker}% - 1px)` }} />}
    </span>
  );
}
